import { useEffect, useState } from "react";
import api from "../lib/api";
import { Clock, AlertCircle } from "lucide-react";

export default function TimeSlotPicker({ date, value, onChange, guests = 1 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!date) { setData(null); return; }
    setLoading(true);
    api.get(`/availability/${date}`, { params: { guests } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [date, guests]);

  if (!date) {
    return <div className="text-sm text-[#4A5568]/60 italic py-2 flex items-center gap-2"><Clock size={14}/> Select a date first</div>;
  }
  if (loading) return <div className="text-sm text-[#5979A9]/60 py-2">Loading available times…</div>;
  if (!data || data.status === "closed" || (data.slots || []).length === 0) {
    return <div className="text-sm text-[#E05947] py-2" data-testid="time-slots-closed">No available times on this date.</div>;
  }

  const bookableSlots = data.slots.filter((s) => s.remaining >= guests);

  return (
    <div data-testid="reservation-time-slots">
      <div className="flex flex-wrap gap-2 py-1">
        {data.slots.map((slot) => {
          const enough = slot.remaining >= guests;
          const isSelected = value === slot.time;
          const cls = isSelected
            ? "bg-[#E05947] border-[#E05947] text-white shadow-md"
            : !enough
              ? "bg-[#E05947]/10 border-[#E05947]/30 text-[#E05947]/70 line-through cursor-not-allowed"
              : slot.status === "limited"
                ? "bg-yellow-50 border-yellow-400 text-yellow-800 hover:bg-yellow-100"
                : "bg-white border-[#F0EAE6] text-[#2D3748] hover:border-[#E05947] hover:text-[#E05947]";
          return (
            <button
              key={slot.time}
              type="button"
              onClick={() => enough && onChange(slot.time)}
              disabled={!enough}
              title={enough ? `${slot.remaining} seats left` : "Fully booked"}
              data-testid={`time-slot-${slot.time}`}
              className={`px-3 py-2 rounded-full text-sm border transition-all ${cls}`}
            >
              {slot.time}
              {slot.status === "limited" && enough && <span className="ml-1.5 text-[10px] opacity-80">· few left</span>}
            </button>
          );
        })}
      </div>
      {bookableSlots.length === 0 && data.next_available && (
        <div className="mt-3 inline-flex items-center gap-2 text-xs text-[#E05947] bg-[#E05947]/10 px-3 py-2 rounded-lg" data-testid="next-available-hint">
          <AlertCircle size={14}/> Fully booked for {guests} guests. Next available: <strong>{data.next_available}</strong>
        </div>
      )}
      {bookableSlots.length === 0 && !data.next_available && (
        <div className="mt-3 inline-flex items-center gap-2 text-xs text-[#E05947]" data-testid="no-availability">
          <AlertCircle size={14}/> Fully booked for {guests} guests on this day.
        </div>
      )}
    </div>
  );
}
